import { Link } from 'react-router-dom';
import { useMemo, useRef, useState } from 'react';
import type { CatalogProduct } from '@/mocks/catalog';
import { useCart } from '@/hooks/useCart';
import { useStaggerReveal } from '@/hooks/useScrollReveal';
import { useFavorites } from '@/hooks/useFavorites';

const LIST_STYLE = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeInLeft {
    from { opacity: 0; transform: translateX(-16px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes hoverProgress {
    from { width: 0%; }
    to   { width: 100%; }
  }
`;

const HOVER_DELAY = 650; // ms before quick view opens

interface Props {
  products: CatalogProduct[];
  viewMode: 'grid' | 'list';
  onQuickView: (product: CatalogProduct) => void;
}

const CatalogGrid = ({ products, viewMode, onQuickView }: Props) => {
  const { addItem, hasItem } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [hoveringId, setHoveringId] = useState<number | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetKey = useMemo(() => products.map(p => p.id).join(','), [products]);
  const { containerRef: gridRef, visibleItems } = useStaggerReveal(
    products.length,
    { threshold: 0.04 },
    resetKey,
  );

  const handleMouseEnter = (p: CatalogProduct) => {
    setHoveringId(p.id);
    hoverTimerRef.current = setTimeout(() => {
      onQuickView(p);
      setHoveringId(null);
    }, HOVER_DELAY);
  };

  const handleMouseLeave = () => {
    setHoveringId(null);
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl mb-4" style={{ background: 'rgba(255,45,120,0.1)', border: '1px solid rgba(255,45,120,0.2)' }}>
          <i className="ri-search-line text-2xl" style={{ color: '#ff2d78' }} />
        </div>
        <h3 className="font-orbitron font-bold text-white mb-2">Ничего не найдено</h3>
        <p className="font-mono text-xs text-white/30">Попробуйте изменить фильтры или поисковый запрос</p>
      </div>
    );
  }

  /* ── LIST VIEW ── */
  if (viewMode === 'list') {
    return (
      <div className="space-y-3" key={resetKey}>
        <style>{LIST_STYLE}</style>
        {products.map((p, i) => (
          <Link
            key={p.id}
            to={`/product/${p.id}`}
            className="card-cyber rounded-xl p-4 flex gap-4 group cursor-pointer transition-all duration-300 relative overflow-hidden"
            style={{
              borderColor: `${p.color}15`,
              animation: `fadeInLeft 0.45s cubic-bezier(0.22,1,0.36,1) ${i * 50}ms both`,
              display: 'flex',
            }}
          >
            {/* Image */}
            <div
              className="w-20 h-20 shrink-0 rounded-xl flex items-center justify-center overflow-hidden relative"
              style={{ background: `linear-gradient(135deg, #0a0a14, ${p.color}10)` }}
            >
              <img
                src={p.image}
                alt={`${p.name} — купить в NeonPharma`}
                title={p.name}
                className="w-16 h-16 object-contain transition-transform duration-300 group-hover:scale-105"
                style={{ filter: `drop-shadow(0 4px 8px ${p.color}40)` }}
              />
              <div
                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl"
                style={{ background: 'rgba(5,5,8,0.7)' }}
              >
                <button
                  onClick={e => { e.preventDefault(); onQuickView(p); }}
                  aria-label={`Быстрый просмотр: ${p.name}`}
                  className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
                  style={{ background: p.color, color: '#050508' }}
                >
                  <i className="ri-eye-line text-sm" />
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 mb-1 flex-wrap">
                {p.badge && (
                  <span className="px-2 py-0.5 rounded-full font-mono text-[10px] font-bold shrink-0" style={{ background: p.badgeColor, color: '#050508' }}>
                    {p.badge}
                  </span>
                )}
                {p.prescription && (
                  <span className="px-2 py-0.5 rounded-full font-mono text-[10px] border" style={{ borderColor: '#f0ff0040', color: '#f0ff00' }}>
                    Рецепт
                  </span>
                )}
              </div>
              <h3 className="font-orbitron font-bold text-sm text-white leading-tight mb-1 truncate">{p.name}</h3>
              <p className="font-mono text-xs text-white/35 mb-1 line-clamp-1">{p.description}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-xs text-white/30">{p.form}</span>
                <span className="font-mono text-xs text-white/20">·</span>
                <span className="font-mono text-xs text-white/30">{p.brand}</span>
                <span className="font-mono text-xs text-white/20">·</span>
                <span className="font-mono text-xs text-white/30">{p.country}</span>
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-[10px]" style={{ color: '#f0ff00' }} />
                  <span className="font-mono text-[10px] text-white/40">{p.rating} ({p.reviews})</span>
                </div>
              </div>
            </div>

            {/* Price + actions */}
            <div className="flex flex-col items-end justify-between shrink-0 gap-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={e => { e.preventDefault(); toggleFavorite({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color: p.color, category: p.category, rating: p.rating, discount: p.discount, inStock: p.inStock }); }}
                  aria-label={isFavorite(p.id) ? `Убрать из избранного: ${p.name}` : `В избранное: ${p.name}`}
                  className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
                  style={{
                    background: isFavorite(p.id) ? 'rgba(255,45,120,0.15)' : 'rgba(255,255,255,0.05)',
                    border: isFavorite(p.id) ? '1px solid rgba(255,45,120,0.4)' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <i className={isFavorite(p.id) ? 'ri-heart-fill' : 'ri-heart-line'} style={{ color: isFavorite(p.id) ? '#ff2d78' : 'rgba(255,255,255,0.3)', fontSize: '13px' }} />
                </button>
                <button
                  onClick={e => { e.preventDefault(); onQuickView(p); }}
                  aria-label={`Быстрый просмотр: ${p.name}`}
                  className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 opacity-0 group-hover:opacity-100"
                  style={{ background: `${p.color}15`, border: `1px solid ${p.color}30` }}
                >
                  <i className="ri-eye-line text-xs" style={{ color: p.color }} />
                </button>
                <div className="text-right">
                  <div className="font-orbitron font-black text-lg" style={{ color: p.color, textShadow: `0 0 8px ${p.color}60` }}>
                    {p.price.toLocaleString()}₽
                  </div>
                  {p.oldPrice && (
                    <div className="font-mono text-xs text-white/25 line-through">{p.oldPrice.toLocaleString()}₽</div>
                  )}
                </div>
              </div>
              <button
                onClick={e => { e.preventDefault(); if (!hasItem(p.id)) addItem({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color: p.color, category: p.category }); }}
                aria-label={hasItem(p.id) ? `${p.name} уже в корзине` : `Добавить в корзину: ${p.name}`}
                className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-full font-mono text-xs cursor-pointer transition-all duration-300"
                style={hasItem(p.id) ? { background: p.color, color: '#050508', boxShadow: `0 0 10px ${p.color}` } : { border: `1px solid ${p.color}`, color: p.color }}
              >
                <i className={hasItem(p.id) ? 'ri-check-line' : 'ri-shopping-bag-line'} />
                <span>{hasItem(p.id) ? 'В корзине' : 'В корзину'}</span>
              </button>
            </div>

            <div className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(90deg, transparent, ${p.color}, transparent)` }} />
          </Link>
        ))}
      </div>
    );
  }

  /* ── GRID VIEW ── */
  return (
    <div
      ref={gridRef as React.RefObject<HTMLDivElement>}
      className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
    >
      <style>{LIST_STYLE}</style>
      {products.map((p, i) => {
        const isHovering = hoveringId === p.id;
        return (
          <Link
            key={p.id}
            to={`/product/${p.id}`}
            onMouseEnter={() => handleMouseEnter(p)}
            onMouseLeave={handleMouseLeave}
            className="card-cyber rounded-2xl overflow-hidden group cursor-pointer relative flex flex-col"
            style={{
              borderColor: isHovering ? `${p.color}50` : `${p.color}15`,
              opacity: visibleItems[i] ? 1 : 0,
              transform: visibleItems[i] ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.96)',
              transition: `opacity 0.5s cubic-bezier(0.22,1,0.36,1) ${i % 3 * 60}ms, transform 0.5s cubic-bezier(0.22,1,0.36,1) ${i % 3 * 60}ms, border-color 0.2s ease`,
              display: 'flex',
            }}
          >
            {/* Hover progress bar — shows countdown to quick view */}
            <div
              className="absolute top-0 left-0 h-0.5 z-20 rounded-full"
              style={{
                background: `linear-gradient(90deg, ${p.color}, #00f5ff)`,
                boxShadow: `0 0 6px ${p.color}`,
                width: isHovering ? '100%' : '0%',
                transition: isHovering ? `width ${HOVER_DELAY}ms linear` : 'width 0.1s ease',
              }}
            />

            {/* Hover hint tooltip */}
            {isHovering && (
              <div
                className="absolute top-2 left-1/2 -translate-x-1/2 z-20 px-2.5 py-1 rounded-full font-mono text-[9px] whitespace-nowrap pointer-events-none"
                style={{
                  background: 'rgba(5,5,8,0.9)',
                  border: `1px solid ${p.color}40`,
                  color: p.color,
                  backdropFilter: 'blur(8px)',
                  animation: 'fadeInUp 0.2s ease both',
                }}
              >
                <i className="ri-eye-line mr-1" />
                Быстрый просмотр...
              </div>
            )}

            {/* Badges */}
            <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
              {p.badge && (
                <span className="px-2.5 py-0.5 rounded-full font-mono text-[10px] font-bold" style={{ background: p.badgeColor, color: '#050508', boxShadow: `0 0 8px ${p.badgeColor}` }}>
                  {p.badge}
                </span>
              )}
              {p.prescription && (
                <span className="px-2.5 py-0.5 rounded-full font-mono text-[10px] border" style={{ borderColor: '#f0ff0050', color: '#f0ff00', background: 'rgba(240,255,0,0.08)' }}>
                  Рецепт
                </span>
              )}
            </div>

            {/* Top-right actions */}
            <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
              <button
                onClick={e => { e.preventDefault(); toggleFavorite({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color: p.color, category: p.category, rating: p.rating, discount: p.discount, inStock: p.inStock }); }}
                aria-label={isFavorite(p.id) ? `Убрать из избранного: ${p.name}` : `В избранное: ${p.name}`}
                className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
                style={{
                  background: isFavorite(p.id) ? 'rgba(255,45,120,0.2)' : 'rgba(0,0,0,0.4)',
                  border: isFavorite(p.id) ? '1px solid rgba(255,45,120,0.5)' : '1px solid rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <i className={isFavorite(p.id) ? 'ri-heart-fill' : 'ri-heart-line'} style={{ color: isFavorite(p.id) ? '#ff2d78' : 'rgba(255,255,255,0.4)', fontSize: '13px' }} />
              </button>
              <button
                onClick={e => { e.preventDefault(); onQuickView(p); }}
                aria-label={`Быстрый просмотр: ${p.name}`}
                className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0"
                style={{
                  background: `${p.color}20`,
                  border: `1px solid ${p.color}50`,
                  backdropFilter: 'blur(4px)',
                }}
              >
                <i className="ri-eye-line text-xs" style={{ color: p.color }} />
              </button>
            </div>

            {/* Image */}
            <div className="relative h-44 flex items-center justify-center overflow-hidden" style={{ background: `linear-gradient(135deg, #0a0a14, ${p.color}08)` }}>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-12 rounded-full opacity-20 group-hover:opacity-40 transition-opacity" style={{ background: p.color, filter: 'blur(16px)' }} />
              <img
                src={p.image}
                alt={`${p.name} — купить в NeonPharma`}
                title={p.name}
                className="w-32 h-32 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1"
                style={{ filter: `drop-shadow(0 8px 16px ${p.color}50)` }}
              />
              <div className="absolute inset-0 flex items-end justify-center pb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20">
                <button
                  onClick={e => { e.preventDefault(); onQuickView(p); }}
                  aria-label={`Быстрый просмотр: ${p.name}`}
                  className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] cursor-pointer transition-all duration-200"
                  style={{
                    background: 'rgba(5,5,8,0.85)',
                    border: `1px solid ${p.color}50`,
                    color: p.color,
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <i className="ri-eye-line" />
                  Быстрый просмотр
                </button>
              </div>
              <div className="absolute top-2 right-2 w-4 h-4" style={{ borderTop: `1px solid ${p.color}60`, borderRight: `1px solid ${p.color}60` }} />
              <div className="absolute bottom-2 left-2 w-4 h-4" style={{ borderBottom: `1px solid ${p.color}60`, borderLeft: `1px solid ${p.color}60` }} />
            </div>

            {/* Info */}
            <div className="p-4 flex flex-col flex-1">
              <h3 className="font-orbitron font-bold text-xs text-white leading-snug mb-1 line-clamp-2">{p.name}</h3>
              <p className="font-mono text-[11px] text-white/35 mb-2 line-clamp-2 leading-relaxed flex-1">{p.description}</p>

              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="font-mono text-[10px] text-white/30 px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>{p.form}</span>
                <span className="font-mono text-[10px] text-white/30 px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>{p.country}</span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-orbitron font-black text-lg" style={{ color: p.color, textShadow: `0 0 8px ${p.color}60` }}>
                    {p.price.toLocaleString()}₽
                  </div>
                  {p.oldPrice && (
                    <div className="font-mono text-[10px] text-white/25 line-through">{p.oldPrice.toLocaleString()}₽</div>
                  )}
                </div>
                <button
                  onClick={e => { e.preventDefault(); if (!hasItem(p.id)) addItem({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color: p.color, category: p.category }); }}
                  aria-label={hasItem(p.id) ? `${p.name} уже в корзине` : `Добавить в корзину: ${p.name}`}
                  className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-full font-mono text-[10px] cursor-pointer transition-all duration-300"
                  style={hasItem(p.id) ? { background: p.color, color: '#050508', boxShadow: `0 0 10px ${p.color}` } : { border: `1px solid ${p.color}`, color: p.color }}
                >
                  <i className={hasItem(p.id) ? 'ri-check-line' : 'ri-shopping-bag-line'} />
                  <span>{hasItem(p.id) ? 'В корзине' : 'В корзину'}</span>
                </button>
              </div>

              <div className="mt-3 pt-2.5 flex items-center gap-1" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                {[1,2,3,4,5].map(s => (
                  <i key={s} className={`text-[9px] ${s <= Math.round(p.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                ))}
                <span className="font-mono text-[10px] text-white/25 ml-1">{p.reviews}</span>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(90deg, transparent, ${p.color}, transparent)` }} />
          </Link>
        );
      })}
    </div>
  );
};

export default CatalogGrid;
